/**
 * Bridge API instants (`start_time` / `end_time` ISO UTC) into `Date` values that
 * survive `@event-calendar/core` ingestion.
 *
 * **Critical pipeline (GMA-1232):** The library runs every supplied `Date` through
 * `createDate(date)` → `_fromLocalDate(date)`, which copies **local** getters
 * (`getFullYear`, `getHours`, …) into **UTC fields** of a new `Date`.
 * Therefore this helper MUST return `new Date(y, mo, d, h, mi, s, ms)` so those
 * local getters equal the client's **wall** clock pieces; after `_fromLocalDate`,
 * the internal event `Date` has **UTC fields** matching that wall clock — which
 * is what `toLocalDate` + `intl.format` use for the grid. Passing wall-clock
 * fields through the static **`Date.UTC` method** then `new Date` here doubled the caregiver offset when browser TZ matched the client zone
 * (staging Apr 2026: 3:14 PM showed as 9:14 AM).
 *
 * **QA / Sentry:** Search `calendar.grid.wall_offset_check` (one per `zone::source`
 * per session) alongside `activity_timezone_resolve`.
 *
 * @param {{ clientId?: number|null, source?: string, resolvedWallZone?: string }} [options] - When `resolvedWallZone` is set (e.g. `formatActivities` batch), skips `resolveActivityTimezone` for that conversion (GMA-1228 / PR #132).
 */
import { addBreadcrumb } from "../config/sentry";
import moment from "moment-timezone";

import { resolveActivityTimezone } from "./activityTimezone";

/** @internal Session-scoped dedupe keys for `calendar.grid.wall_offset_check` (one breadcrumb per zone+source per browser session in production). */
const gridWallOffsetBreadcrumbKeys = new Set();

/** @internal Clears session dedupe keys between Jest runs (unit + integration suites). */
export function __resetGridWallOffsetBreadcrumbDedupeForTests() {
  gridWallOffsetBreadcrumbKeys.clear();
}

/**
 * @param {string|Date|undefined|null} isoOrDate - Activity start/end from API (ISO string recommended).
 * @param {string|undefined|null} clientTimeZone - IANA zone from selected client (e.g. America/New_York).
 * @param {{ clientId?: number|null, source?: string }} options
 * @returns {Date}
 */
export function activityInstantToEventCalendarDate(
  isoOrDate,
  clientTimeZone,
  options = {},
) {
  if (isoOrDate === null || isoOrDate === undefined || isoOrDate === "") {
    return new Date(NaN);
  }
  const source = typeof options.source === "string" ? options.source : "grid";
  const clientId = options.clientId ?? null;
  const useBatchZone =
    typeof options.resolvedWallZone === "string" &&
    options.resolvedWallZone.length > 0;

  const zone = useBatchZone
    ? options.resolvedWallZone
    : resolveActivityTimezone(clientTimeZone, {
        source,
        clientId,
      }).zone;
  const m = moment(isoOrDate).tz(zone);
  if (!m.isValid()) {
    return new Date(NaN);
  }

  const d = new Date(
    m.year(),
    m.month(),
    m.date(),
    m.hour(),
    m.minute(),
    m.second(),
    m.millisecond(),
  );

  const breadcrumbKey = `${zone}::${source}`;
  if (!gridWallOffsetBreadcrumbKeys.has(breadcrumbKey)) {
    gridWallOffsetBreadcrumbKeys.add(breadcrumbKey);
    try {
      const browserOffset =
        typeof new Date().getTimezoneOffset === "function"
          ? new Date().getTimezoneOffset()
          : null;
      addBreadcrumb("calendar.grid.wall_offset_check", {
        category: "calendar.grid",
        level: "info",
        data: {
          ticket: "GMA-1232",
          zone,
          source,
          wallHour: m.hour(),
          wallMinute: m.minute(),
          helperLocalHour: d.getHours(),
          helperUtcHour: d.getUTCHours(),
          browserTimezoneOffsetMin: browserOffset,
        },
      });
    } catch {
      /* Sentry unavailable in some test environments */
    }
  }

  return d;
}
